import PropTypes from "prop-types";
import * as React from "react";
import { Checkbox, Chip, Spinner, TextInput, Typography } from "../../themeV2";
import { useDebounce } from "../../hooks";
import { useUserSearch } from "../Dashboard/AdvancedSettings/hooks";
import UserInvite from "../UserInvite";
import styles from "./AddShopDialog.module.css";
import { ACTION } from "./addShopReducer";

function PayoutSection({
  payoutSameAsOwner,
  payoutEmail,
  payoutUser,
  payoutStatus,
  dispatch,
}) {
  const [debouncedEmail, setDebouncedEmail] = React.useState("");

  useDebounce(
    () => {
      setDebouncedEmail(payoutEmail);
    },
    500,
    [payoutEmail],
  );

  const { users, isLoading } = useUserSearch(
    payoutSameAsOwner ? "" : debouncedEmail,
  );

  React.useEffect(() => {
    if (payoutSameAsOwner) return;

    if (isLoading) {
      dispatch({
        type: ACTION.SET_PAYOUT_USER,
        user: null,
        status: "loading",
      });
      return;
    }

    if (!debouncedEmail || debouncedEmail.trim().length < 4) {
      dispatch({ type: ACTION.SET_PAYOUT_USER, user: null, status: "idle" });
      return;
    }

    const exactMatch = users.find(
      (u) => u.email.toLowerCase() === debouncedEmail.trim().toLowerCase(),
    );

    if (exactMatch) {
      dispatch({
        type: ACTION.SET_PAYOUT_USER,
        user: exactMatch,
        status: "found",
      });
    } else {
      dispatch({
        type: ACTION.SET_PAYOUT_USER,
        user: null,
        status: "not_found",
      });
    }
  }, [users, isLoading, debouncedEmail, payoutSameAsOwner, dispatch]);

  return (
    <section className={styles.section}>
      <Typography variant="title-md" as="h3">
        Payout recipient
      </Typography>

      <Checkbox
        checked={payoutSameAsOwner}
        onCheckedChange={(checked) =>
          dispatch({
            type: ACTION.SET_FIELD,
            field: "payoutSameAsOwner",
            value: checked,
          })
        }
        label="Same as shop owner"
      />

      {!payoutSameAsOwner && (
        <>
          <TextInput
            label="Payout Recipient Email"
            type="email"
            value={payoutEmail}
            onChange={(e) =>
              dispatch({
                type: ACTION.SET_FIELD,
                field: "payoutEmail",
                value: e.target.value,
              })
            }
            trailing={
              payoutStatus === "loading" ? (
                <Spinner size="sm" label="Searching for user" />
              ) : null
            }
          />

          <div aria-live="polite">
            {payoutStatus === "found" && payoutUser && (
              <div className={styles.userResult}>
                <Chip variant="assist" size="sm" label="User found" />
                <Typography variant="body-lg">
                  {payoutUser.first_name} {payoutUser.last_name}
                </Typography>
              </div>
            )}

            {payoutStatus === "not_found" && (
              <div className={styles.notFoundRow}>
                <Typography variant="body-lg" color="error">
                  No user found with that exact email.
                </Typography>
                <UserInvite
                  size="small"
                  variant="outlined"
                  label="Invite User"
                />
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

PayoutSection.propTypes = {
  payoutSameAsOwner: PropTypes.bool.isRequired,
  payoutEmail: PropTypes.string.isRequired,
  payoutUser: PropTypes.object,
  payoutStatus: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default React.memo(PayoutSection);
